/**
 * Os embriões transferidos, no PDF da fatura.
 *
 * O papel dizia só "Embriões transferidos: 10". Pedido do laboratório
 * (29/10/2026): o cliente quer ver QUAIS — receptora, embrião, doadora e
 * touro — e, quando a cobrança é por prenhez, quais dessas ficaram prenhes.
 * Sem isso ele confere a fatura contra um número que não tem como checar.
 *
 * O resultado sai da DG que o serviço tiver: a coluna do DG30 e a do DG60 só
 * aparecem quando houver ao menos um resultado lançado nelas.
 */

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * Junta as transferências APROVADAS com as checagens de DG que apontam para
 * elas. A doadora e o touro vêm da própria transferência e, faltando lá (o
 * descongelado do estoque não grava), do embrião.
 */
export const linhasDosTransferidos = (transferencias = [], checagens = []) => {
  const resultado = (id, tipo) =>
    checagens.find(c => c.te_transfer_id === id && c.check_type === tipo)?.result || null;

  return transferencias
    .filter(t => t.status === 'APROVADA')
    .map(t => ({
      embriao: t.embryo_number ?? t.embryo?.service_number ?? null,
      doadora: t.donor_name || t.embryo?.donor?.identifier || t.embryo?.donor_identifier || null,
      touro: t.bull_name || t.embryo?.bull_name || null,
      receptora: t.recipient_identifier || null,
      dg30: resultado(t.id, 'DG30'),
      dg60: resultado(t.id, 'DG60'),
    }));
};

const ROTULO = { PRENHE: 'Prenhe', VAZIA: 'Vazia', INCONCLUSIVO: 'Inconclusivo', ABORTOU: 'Abortou' };
const pct = (parte, todo) => (todo > 0 ? ` (${Math.round((parte / todo) * 100)}%)` : '');

/** A seção do PDF. Vazia quando não há transferido. */
export const secaoDosTransferidos = (linhas = []) => {
  if (linhas.length === 0) return '';

  const temDG30 = linhas.some(l => l.dg30);
  const temDG60 = linhas.some(l => l.dg60);
  const prenhes30 = linhas.filter(l => l.dg30 === 'PRENHE').length;
  const prenhes60 = linhas.filter(l => l.dg60 === 'PRENHE').length;
  const td = 'padding:4px;border-bottom:1px solid #e2e8f0';
  const celDG = (r) => `<td style="${td};${r === 'PRENHE' ? 'font-weight:bold;color:#047857' : ''}">${r ? ROTULO[r] || esc(r) : '—'}</td>`;

  const resumo = [
    `<strong>Transferidos:</strong> ${linhas.length}`,
    temDG30 && `<strong>Prenhes no DG30:</strong> ${prenhes30}${pct(prenhes30, linhas.length)}`,
    temDG60 && `<strong>Confirmadas no DG60:</strong> ${prenhes60}${pct(prenhes60, linhas.length)}`,
  ].filter(Boolean).join(' &nbsp;·&nbsp; ');

  return `
    <div>
      <div class="section-title">EMBRIÕES TRANSFERIDOS</div>
      <table style="width:100%;border-collapse:collapse;font-size:12px">
        <thead>
          <tr style="text-align:left;border-bottom:1px solid #cbd5e1">
            <th style="padding:4px">#</th><th style="padding:4px">Embrião</th>
            <th style="padding:4px">Doadora × Touro</th><th style="padding:4px">Receptora</th>
            ${temDG30 ? '<th style="padding:4px">DG30</th>' : ''}
            ${temDG60 ? '<th style="padding:4px">DG60</th>' : ''}
          </tr>
        </thead>
        <tbody>
          ${linhas.map((l, i) => `
            <tr>
              <td style="${td}">${i + 1}</td>
              <td style="${td}">${l.embriao != null ? `#${esc(l.embriao)}` : '—'}</td>
              <td style="${td}">${esc(l.doadora || '—')} × ${esc(l.touro || '—')}</td>
              <td style="${td}">${esc(l.receptora || '—')}</td>
              ${temDG30 ? celDG(l.dg30) : ''}
              ${temDG60 ? celDG(l.dg60) : ''}
            </tr>`).join('')}
        </tbody>
      </table>
      <p style="font-size:12px;margin-top:8px">${resumo}</p>
    </div>`;
};
